import { createTheme } from '@mui/material/styles';
import * as variables from './_variables.module.scss';

const {
  mmcRegularFont,
  mmcMediumFont,
  mmcBoldFont,

  mmcRed,
  mmcWarningYellow,
  offWhite,
  commonWhite,
  commonBlack,

  mmcAnthraciteGrey,
  mmcColdGrey,
  mmcConcreteGrey,
} = variables.default;

const theme = createTheme({
  palette: {
    common: {
      white: commonWhite,
      black: commonBlack,
    },
    primary: {
      main: commonBlack,
    },
    secondary: {
      main: mmcRed,
    },
    tertiary: {
      main: mmcAnthraciteGrey,
    },
    warning: {
      main: mmcWarningYellow,
    },
    error: {
      main: mmcRed,
    },
    text: {
      primary: commonBlack,
      secondary: mmcAnthraciteGrey,
    },
    grey: {
      anthracite: mmcAnthraciteGrey, // darker
      cold: mmcColdGrey, // dark
      concrete: mmcConcreteGrey, // light
    },
    offWhite: {
      main: offWhite,
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: mmcBoldFont,
          fontStyle: 'normal',
          fontWeight: 700,
          fontSize: '12px',
          lineHeight: '18px',
          textTransform: 'uppercase',
          letterSpacing: '3px',
          borderRadius: '0px',
          paddingTop: '15px',
          paddingBottom: '15px',
          '@media (min-width: 600px)': {
            fontSize: '14px',
            lineHeight: '20px',
          },
          ':disabled': {
            backgroundColor: mmcColdGrey,
            color: commonWhite,
          },
        },
        outlinedSecondary: { borderColor: mmcRed },
        containedSecondary: { borderColor: mmcRed },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '0px',
          boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.15)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: mmcRegularFont,
          color: mmcAnthraciteGrey,
          fontSize: '14px',
          '&.Mui-selected': {
            fontFamily: mmcMediumFont,
            color: commonBlack,
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          fontFamily: mmcRegularFont,
          color: mmcAnthraciteGrey,
          fontSize: '12px',
          '&.Mui-selected': {
            fontFamily: mmcMediumFont,
            color: commonBlack,
          },
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          fontFamily: mmcBoldFont,
          fontStyle: 'normal',
          fontWeight: 700,
          fontSize: '14px',
          lineHeight: '20px',
          letterSpacing: '3px',
          color: commonBlack,
        },
        icon: {
          fill: commonBlack,
        },
      },
    },
    MuiCollapse: {
      styleOverrides: {
        wrapperInner: {
          width: '100%',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: '32px',
          fontFamily: mmcBoldFont,
          color: mmcAnthraciteGrey,
          fontSize: '14px',
          letterSpacing: '1px',
          padding: '6px 16px',
          '&.Mui-selected': {
            color: commonBlack,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: '32px',
        },
        indicator: {
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: 'transparent',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          height: '2em',
          width: '2em',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          fontFamily: mmcMediumFont,
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontFamily: mmcRegularFont,
          fontSize: 14,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontFamily: mmcRegularFont,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontFamily: mmcMediumFont,
          paddingBottom: '14px',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: mmcRegularFont,
          letterSpacing: '2px',
          fontSize: '12px',
          top: '8px',
        },
        shrink: {
          top: '4px',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          '@media (min-width: 600px)': {
            padding: '10px 50px',
            minHeight: '110px',
          },
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          heroHeading: 'h1',
          introCopy: 'p',
          bodyCopy: 'p',
          bodySmall: 'p',
          smallType: 'p',
          inputsAndOptions: 'p',
          fileName: 'p',
          buttonsAndNav: 'p',
          labelBigRegular: 'p',
          labelBigMedium: 'p',
          labelSmallRegular: 'p',
          labelSmallMedium: 'p',
          copyright: 'p',
        },
      },
      styleOverrides: {
        root: {
          fontFamily: mmcMediumFont,
        },
        h1: {
          fontFamily: mmcBoldFont,
          fontStyle: 'normal',
          fontWeight: 700,
          fontSize: '63px',
          lineHeight: '70px',
        },
        h2: {
          fontFamily: mmcBoldFont,
          fontStyle: 'normal',
          fontWeight: 700,
          fontSize: '42px',
          lineHeight: '45px',
        },
        h3: {
          fontFamily: mmcBoldFont,
          fontStyle: 'normal',
          fontWeight: 700,
          fontSize: '36px',
          lineHeight: '40px',
        },
        h4: {
          fontFamily: mmcBoldFont,
          fontStyle: 'normal',
          fontWeight: 700,
          fontSize: '24px',
          lineHeight: '30px',
        },
        h5: {
          fontFamily: mmcBoldFont,
          fontStyle: 'normal',
          fontWeight: 700,
          fontSize: '21px',
          lineHeight: '25px',
        },
        h6: {
          fontFamily: mmcMediumFont,
          fontStyle: 'normal',
          fontWeight: 500,
          fontSize: '20px',
          lineHeight: '25px',
        },
        heroHeading: {
          fontFamily: mmcBoldFont,
          fontStyle: 'normal',
          fontWeight: 700,
          fontSize: '84px',
          lineHeight: '90px',
        },
        introCopy: {
          fontFamily: mmcMediumFont,
          fontStyle: 'normal',
          fontWeight: 400,
          fontSize: '21px',
          lineHeight: '36px',
          color: mmcAnthraciteGrey,
        },
        bodyCopy: {
          fontFamily: mmcRegularFont,
          fontStyle: 'normal',
          fontWeight: 400,
          fontSize: '16px',
          lineHeight: '28px',
          color: mmcAnthraciteGrey,
        },
        bodySmall: {
          fontFamily: mmcRegularFont,
          fontStyle: 'normal',
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '26px',
          color: mmcAnthraciteGrey,
        },
        smallType: {
          fontFamily: mmcRegularFont,
          fontStyle: 'normal',
          fontWeight: 400,
          fontSize: '12px',
          lineHeight: '20px',
          color: mmcAnthraciteGrey,
        },
        inputsAndOptions: {
          fontFamily: mmcMediumFont,
          fontStyle: 'normal',
          fontWeight: 500,
          fontSize: '12px',
          lineHeight: '20px',
        },
        fileName: {
          fontFamily: mmcBoldFont,
          fontStyle: 'normal',
          fontWeight: 700,
          fontSize: '14px',
          lineHeight: '20px',
        },
        buttonsAndNav: {
          fontFamily: mmcBoldFont,
          fontStyle: 'normal',
          fontWeight: 700,
          fontSize: '14px',
          lineHeight: '18px',
          letterSpacing: '3px',
          textTransform: 'uppercase',
        },
        labelBigRegular: {
          fontFamily: mmcRegularFont,
          fontStyle: 'normal',
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '18px',
          letterSpacing: '2px',
          textTransform: 'uppercase',
          color: mmcAnthraciteGrey,
        },
        labelBigMedium: {
          fontFamily: mmcMediumFont,
          fontStyle: 'normal',
          fontWeight: 500,
          fontSize: '14px',
          lineHeight: '18px',
          letterSpacing: '2px',
          textTransform: 'uppercase',
          color: mmcAnthraciteGrey,
        },
        labelSmallRegular: {
          fontFamily: mmcRegularFont,
          fontStyle: 'normal',
          fontWeight: 400,
          fontSize: '12px',
          lineHeight: '16px',
          letterSpacing: '2px',
          textTransform: 'uppercase',
          color: mmcAnthraciteGrey,
        },
        labelSmallMedium: {
          fontFamily: mmcMediumFont,
          fontStyle: 'normal',
          fontWeight: 500,
          fontSize: '12px',
          lineHeight: '16px',
          letterSpacing: '2px',
          textTransform: 'uppercase',
          color: mmcAnthraciteGrey,
        },
        copyright: {
          fontFamily: mmcRegularFont,
          fontStyle: 'normal',
          fontWeight: 400,
          fontSize: '12px',
          lineHeight: '20px',
          color: mmcAnthraciteGrey,
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      mobile: 0,
      tablet: 640,
      smallLaptop: 1024,
      largeLaptop: 1440,
      desktop: 1920,
    },
  },
});

// responsive font sizes
theme.typography.heroHeading = {
  [theme.breakpoints.down('lg')]: {
    fontSize: '36px',
    lineHeight: '42px',
  },
};
theme.typography.h1 = {
  [theme.breakpoints.down('lg')]: {
    fontSize: '32px',
    lineHeight: '36px',
  },
};
theme.typography.h2 = {
  [theme.breakpoints.down('lg')]: {
    fontSize: '28px',
    lineHeight: '33px',
  },
};
theme.typography.h3 = {
  [theme.breakpoints.down('lg')]: {
    fontSize: '24px',
    lineHeight: '29px',
  },
};
theme.typography.h4 = {
  [theme.breakpoints.down('lg')]: {
    fontSize: '21px',
    lineHeight: '26px',
  },
};
theme.typography.h5 = {
  [theme.breakpoints.down('lg')]: {
    fontSize: '20px',
    lineHeight: '25px',
  },
};
theme.typography.h6 = {
  [theme.breakpoints.down('lg')]: {
    fontSize: '20px',
    lineHeight: '25px',
  },
};
theme.typography.introCopy = {
  [theme.breakpoints.down('lg')]: {
    fontSize: '14px',
    lineHeight: '22px',
  },
};
theme.typography.bodyCopy = {
  [theme.breakpoints.down('lg')]: {
    fontSize: '12px',
    lineHeight: '20px',
  },
};
theme.typography.bodySmall = {
  [theme.breakpoints.down('lg')]: {
    fontSize: '11px',
    lineHeight: '19px',
  },
};
theme.typography.smallType = {
  [theme.breakpoints.down('lg')]: {
    fontSize: '10px',
    lineHeight: '18px',
  },
};
theme.typography.inputsAndOptions = {
  [theme.breakpoints.down('lg')]: {
    fontFamily: mmcBoldFont,
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '20px',
  },
};
theme.typography.fileName = {
  [theme.breakpoints.down('lg')]: {
    fontSize: '13px',
    lineHeight: '19px',
  },
};
theme.typography.buttonsAndNav = {
  [theme.breakpoints.down('lg')]: {
    fontSize: '12px',
    lineHeight: '18px',
  },
};
theme.typography.labelBigRegular = {
  [theme.breakpoints.down('lg')]: {
    fontSize: '12px',
    lineHeight: '16px',
  },
};
theme.typography.labelBigMedium = {
  [theme.breakpoints.down('lg')]: {
    fontSize: '12px',
    lineHeight: '16px',
  },
};
theme.typography.labelSmallRegular = {
  [theme.breakpoints.down('lg')]: {
    fontSize: '10px',
    lineHeight: '14px',
  },
};
theme.typography.labelSmallMedium = {
  [theme.breakpoints.down('lg')]: {
    fontSize: '10px',
    lineHeight: '14px',
  },
};

export default theme;
